import React, { Fragment, useEffect, useState } from 'react'
import { Form } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { LogoIcon } from '../../../shared/components/Icon/Icon'
import { supabase } from '../../../lib/supabase'

const sanitizeNextPath = (next) => {
  if (!next || typeof next !== 'string') return null
  if (next.startsWith('http://') || next.startsWith('https://')) return null
  return next.startsWith('/') ? next : `/${next}`
}

const Confirm = () => {
  const navigate = useNavigate()
  const [status, setStatus] = useState('pending')
  const [error, setError] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [nextPath, setNextPath] = useState(null)

  useEffect(() => {
    const processRedirect = async () => {
      const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''))
      const searchParams = new URLSearchParams(window.location.search)

      const typeParam = hashParams.get('type') || searchParams.get('type') || ''
      const normalizedType = typeParam.toLowerCase()
      const accessToken = hashParams.get('access_token')
      const refreshToken = hashParams.get('refresh_token')
      const code = searchParams.get('code') || hashParams.get('code')
      const next = sanitizeNextPath(searchParams.get('next') || hashParams.get('next'))
      if (next) {
        setNextPath(next)
      }

      try {
        if (code) {
          const { error } = await supabase.auth.exchangeCodeForSession(code)
          if (error) throw error
          if (normalizedType === 'recovery') {
            setError('')
            setStatus('reset')
          } else {
            setError('')
            setStatus('confirmed')
            if (next) {
              setTimeout(() => navigate(next, { replace: true }), 1500)
            }
          }
          return
        }

        if (accessToken && refreshToken) {
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          })
          if (error) throw error

          if (normalizedType === 'recovery') {
            setError('')
            setStatus('reset')
          } else {
            setError('')
            setStatus('confirmed')
            if (next) {
              setTimeout(() => navigate(next, { replace: true }), 1500)
            }
          }
          return
        }

        if (typeParam) {
          if (normalizedType === 'recovery') {
            setError('')
            setStatus('reset')
          } else {
            setError('')
            setStatus('confirmed')
            if (next) {
              setTimeout(() => navigate(next, { replace: true }), 1500)
            }
          }
          return
        }

        setStatus('error')
        setError('This confirmation link is incomplete or has already been used.')
      } catch (err) {
        console.error('Error handling Supabase confirmation:', err)
        setStatus('error')
        setError(err.message || 'We could not verify this link. It may have expired.')
      }
    }

    processRedirect()
  }, [navigate])

  const handlePasswordUpdate = async (event) => {
    event.preventDefault()
    setError('')

    if (password.length < 8) {
      setError('Your new password must be at least 8 characters long.')
      return
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match. Please try again.')
      return
    }

    setIsSubmitting(true)
    try {
      const { error } = await supabase.auth.updateUser({ password })
      if (error) {
        setError(error.message || 'We could not update your password. Please try again.')
        return
      }

      setStatus('reset-success')
      setTimeout(() => {
        navigate(nextPath || '/auth/login', { replace: true })
      }, 1800)
    } catch (err) {
      console.error('Password update failed:', err)
      setError('An unexpected error occurred. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const renderContent = () => {
    if (status === 'pending') {
      return (
        <div className="text-center">
          <div className="flex items-center gap-2 mb-3 justify-center">
            <LogoIcon color='#FEC51C' />
            <span className='font-medium text__20 text-text-primary'>Kinn</span>
          </div>
          <h4 className='font-medium text__28 mb-2'>Hold tight...</h4>
          <p className='text__14 text-text-primary-secondary'>We're confirming the secure link you opened.</p>
        </div>
      )
    }

    if (status === 'confirmed') {
      return (
        <div className="text-center">
          <div className="flex items-center gap-2 mb-3 justify-center">
            <LogoIcon color='#FEC51C' />
            <span className='font-medium text__20 text-text-primary'>Kinn</span>
          </div>
          <h4 className='font-medium text__32 mb-2'>You're all set!</h4>
          <p className='text__16 text-text-primary-secondary mb-8'>
            Your email is confirmed. You can now sign in and continue using Kinn.
          </p>
          <button
            onClick={() => navigate('/auth/login')}
            className='inline-block cursor-pointer font-medium text__16 text-white !py-[15px] bg-surface-default !border-shell-border btnClass px-8'
          >
            Go to login
          </button>
        </div>
      )
    }

    if (status === 'reset') {
      return (
        <Fragment>
          <div className="text-center mb-8">
            <div className="flex items-center gap-2 mb-3 justify-center">
              <LogoIcon color='#FEC51C' />
              <span className='font-medium text__20 text-text-primary'>Kinn</span>
            </div>
            <h4 className='font-medium text__32 mb-2'>Create a new password</h4>
            <p className='text__14 text-text-primary-secondary'>Choose a strong password to secure your Kinn account.</p>
          </div>

          <Form onSubmit={handlePasswordUpdate}>
            <Form.Group className='mb-4' controlId="reset-password">
              <Form.Label className='font-normal text__14 text-[#A3A3A3]'>New password</Form.Label>
              <Form.Control
                type="password"
                placeholder="********"
                className='font-medium text__14 bg-[#FAFAFA] h-[54px] rounded-[20px] px-3 outline-none shadow-none focus:outline-none focus:shadow-none border-[#F5F5F5] focus:border-[#F5F5F5] focus:bg-[#FAFAFA]'
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isSubmitting}
                minLength={8}
                required
              />
            </Form.Group>

            <Form.Group className='mb-4' controlId="reset-password-confirm">
              <Form.Label className='font-normal text__14 text-[#A3A3A3]'>Confirm password</Form.Label>
              <Form.Control
                type="password"
                placeholder="Re-enter password"
                className='font-medium text__14 bg-[#FAFAFA] h-[54px] rounded-[20px] px-3 outline-none shadow-none focus:outline-none focus:shadow-none border-[#F5F5F5] focus:border-[#F5F5F5] focus:bg-[#FAFAFA]'
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={isSubmitting}
                minLength={8}
                required
              />
            </Form.Group>

            {error && (
              <p className='text__14 text-red-500 text-center mb-4'>{error}</p>
            )}

            <button
              type="submit"
              className='inline-block cursor-pointer text-center font-medium text__16 text-white !py-[15px] bg-surface-default !border-shell-border btnClass w-full disabled:opacity-60 disabled:cursor-not-allowed'
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Updating password...' : 'Save new password'}
            </button>
          </Form>
        </Fragment>
      )
    }

    if (status === 'reset-success') {
      return (
        <div className="text-center">
          <div className="flex items-center gap-2 mb-3 justify-center">
            <LogoIcon color='#FEC51C' />
            <span className='font-medium text__20 text-text-primary'>Kinn</span>
          </div>
          <h4 className='font-medium text__32 mb-2'>Password updated</h4>
          <p className='text__16 text-text-primary-secondary mb-8'>
            Your password has been reset. Redirecting you back to Kinn.
          </p>
          <button
            onClick={() => navigate(nextPath || '/auth/login', { replace: true })}
            className='inline-block cursor-pointer font-medium text__16 text-white !py-[15px] bg-surface-default !border-shell-border btnClass px-8'
          >
            Continue
          </button>
        </div>
      )
    }

    return (
      <div className="text-center">
        <div className="flex items-center gap-2 mb-3 justify-center">
          <LogoIcon color='#FEC51C' />
          <span className='font-medium text__20 text-text-primary'>Kinn</span>
        </div>
        <h4 className='font-medium text__32 mb-2'>Link not valid</h4>
        <p className='text__14 text-text-primary-secondary mb-6'>
          {error || 'This confirmation link has expired or was already used. Request a new one from the login screen.'}
        </p>
        <button
          onClick={() => navigate('/auth/login')}
          className='inline-block cursor-pointer font-medium text__16 text-white !py-[15px] bg-surface-default !border-shell-border btnClass px-8'
        >
          Back to login
        </button>
      </div>
    )
  }

  return (
    <Fragment>
      {status !== 'reset' && status !== 'reset-success' && error && (
        <p className='sr-only' aria-live="assertive">{error}</p>
      )}
      {renderContent()}
    </Fragment>
  )
}

export default Confirm
